import React, { useState, useEffect } from 'react';
import _, { clone } from 'lodash';
import { useSetRecoilState } from 'recoil';
import { css } from '@emotion/react';
import { Input, PrimaryButton, Switch } from '@components/atoms';
import { dialogContentsState } from '@components/templates/Dialog';
import { getFetcher } from '../../../../App';
import useStaticDialog from '../../../../utils/hooks/useStaticDialog';
import SettingCommonMethods from '../../../../utils/settingCommon';
import {
  InputList,
  SettingBox,
  SettingList,
  SettingWrapper,
  StyledReportTypeList,
  StyledReportWrap,
  SwButtonSection,
} from './style';
import { submitDebounce } from 'utils/lib';

interface sendToPacsState {
  [key: string]: boolean;
}

const ADSendToPacs = () => {
  const { submitConfirmDialog, confirmTurnAutomaticallyOnOffDialog } = useStaticDialog();
  const { errorHandler } = SettingCommonMethods();
  const setDialogContents = useSetRecoilState(dialogContentsState);

  const [data, setData] = useState<sendToPacsState>({
    relayImage: true,
    relayPacsReport: true,
    sendAgeRelatedExpertReportToPacs: true,
    sendGeneralExpertReportToPacs: true,
    sendPatientReportToPacs: true,
    sendSeizureExpertReportToPacs: true,
    sendThicknessExpertReportToPacs: true,
    sendVolumeExpertReportToPacs: true,
  });
  const [backupData, setBackupData] = useState<sendToPacsState>({
    relayImage: true,
    relayPacsReport: true,
    sendAgeRelatedExpertReportToPacs: true,
    sendGeneralExpertReportToPacs: true,
    sendPatientReportToPacs: true,
    sendSeizureExpertReportToPacs: true,
    sendThicknessExpertReportToPacs: true,
    sendVolumeExpertReportToPacs: true,
  });

  /**
   * 제품별 각각 다 들어갈 수 있는 모듈로 제품에 따라
   * api 경로 다르게 반환 하기 위한 함수
   */
  const getAPIUrl = () => {
    return `api/setting/ad/pacs`;
  };

  /**
   * 사용자 가 값 변경시 동작
   * @param key
   * @param value
   */

  const setDataValue = (key: string, value: boolean) => {
    setData((prevState) => {
      let _clone = clone(prevState);
      _clone[key as string] = value;
      return _clone;
    });
  };

  /**
   * 스위치 변경 컨펌
   * @param key
   * @param value
   */
  const confirmTurnOnOff = (key: string, value: boolean) => {
    const turn = value ? 'on' : 'off';

    //먼저 바꾸고 컨펌에 따라 결과 확정
    setData((prevState) => {
      let _clone = clone(prevState);
      _clone[key as string] = value;
      return _clone;
    });

    confirmTurnAutomaticallyOnOffDialog(
      turn,
      () => null,
      () => {
        setDataValue(key, !value);
      },
    );
  };

  /**
   * submit confirm cb
   * data 전송
   */
  const postDataCB = async () => {
    // valid 체크
    const url = getAPIUrl();
    const token = localStorage.getItem('PROD_ADMIN_ACCESS_TOKEN') || undefined;

    await getFetcher({
      method: 'POST',
      accessToken: token,
      body: {
        ...data,
      },
    })(url)
      .then((r) => {
        if (r.code === '200') {
          setDialogContents({
            type: 'plain',
            body: `It has been reflected.`,
            footer: (
              <PrimaryButton
                onClick={async () => {
                  setDialogContents({ type: 'hidden' });
                }}
              >
                OK
              </PrimaryButton>
            ),
          });
          setBackupData(data);
        } else {
          errorHandler(r);
        }
      })
      .catch((e) => {
        console.log(e, 'e');
      });
  };

  /**
   * get send to pacs data
   */
  const getData = async () => {
    const url = getAPIUrl(); // ok
    const token = localStorage.getItem('PROD_ADMIN_ACCESS_TOKEN') || undefined;

    await getFetcher({
      method: 'GET',
      accessToken: token,
    })(url)
      .then((r) => {
        if (r.code === '200') {
          setData(r.result);
          setBackupData(r.result);
        } else {
          errorHandler(r);
        }
      })
      .catch((e) => {
        console.log(e, 'e');
      });
  };

  useEffect(() => {
    (async () => {
      await getData();
    })();
  }, []);

  return (
    <>
      <label>Send to PACS</label>
      <StyledReportWrap>
        <SettingWrapper>
          <strong>Report</strong>
          <SettingBox>
            <SettingList>
              <li>
                <InputList>
                  <li className={'sub-title'}>Send automatically</li>
                </InputList>
                <InputList>
                  <span className={'send-off'}>Off</span>
                  <Switch
                    defaultValue={data.relayPacsReport}
                    onChangeValue={(e) => confirmTurnOnOff('relayPacsReport', e)}
                  />
                  <span>On</span>
                </InputList>
              </li>

              <li>
                <InputList>
                  <li className={'sub-title'}>Send report type</li>
                </InputList>
                <StyledReportTypeList>
                  <li>
                    <Input
                      type={'checkbox'}
                      name={'reportPatient'}
                      checked={data.sendPatientReportToPacs}
                      disabled={!data.relayPacsReport}
                      onChange={(e) => setDataValue('sendPatientReportToPacs', e.target.checked)}
                    >
                      Patient
                    </Input>
                  </li>
                  <li>
                    <Input
                      type={'checkbox'}
                      name={'reportExpertGeneral'}
                      checked={data.sendGeneralExpertReportToPacs}
                      disabled={!data.relayPacsReport}
                      onChange={(e) => setDataValue('sendGeneralExpertReportToPacs', e.target.checked)}
                    >
                      Expert- General
                    </Input>
                  </li>
                  <li>
                    <Input
                      type={'checkbox'}
                      name={'reportExpertAgeRelated'}
                      checked={data.sendAgeRelatedExpertReportToPacs}
                      disabled={!data.relayPacsReport}
                      onChange={(e) => setDataValue('sendAgeRelatedExpertReportToPacs', e.target.checked)}
                    >
                      Expert- Age related
                    </Input>
                  </li>
                  <li>
                    <Input
                      type={'checkbox'}
                      name={'reportExpertSeizure'}
                      checked={data.sendSeizureExpertReportToPacs}
                      disabled={!data.relayPacsReport}
                      onChange={(e) => setDataValue('sendSeizureExpertReportToPacs', e.target.checked)}
                    >
                      Expert- Seizure
                    </Input>
                  </li>
                  <li>
                    <Input
                      type={'checkbox'}
                      name={'reportExpertAllVolume'}
                      checked={data.sendVolumeExpertReportToPacs}
                      disabled={!data.relayPacsReport}
                      onChange={(e) => setDataValue('sendVolumeExpertReportToPacs', e.target.checked)}
                    >
                      Expert- All Volume
                    </Input>
                  </li>
                  <li>
                    <Input
                      type={'checkbox'}
                      name={'reportExpertAllThickness'}
                      checked={data.sendThicknessExpertReportToPacs}
                      disabled={!data.relayPacsReport}
                      onChange={(e) => setDataValue('sendThicknessExpertReportToPacs', e.target.checked)}
                    >
                      Expert- All Thickness
                    </Input>
                  </li>
                </StyledReportTypeList>
              </li>
            </SettingList>
          </SettingBox>
        </SettingWrapper>
        <SettingWrapper>
          <strong>Image</strong>
          <SettingBox>
            <SettingList>
              <li>
                <InputList>
                  <li className={'sub-title'}>Send automatically</li>
                </InputList>
                <InputList>
                  <InputList>
                    <span className={'send-off'}>Off</span>

                    <Switch defaultValue={data.relayImage} onChangeValue={(e) => confirmTurnOnOff('relayImage', e)} />

                    <span>On</span>
                  </InputList>
                </InputList>
              </li>
              <li className={'guide-text'}>*Image : MRI + Mask Layer</li>
            </SettingList>
          </SettingBox>
        </SettingWrapper>
      </StyledReportWrap>
      <SwButtonSection>
        <PrimaryButton
          styles={css`
            width: 145px;
            height: 48px;
            font-size: 18px;
          `}
          onClick={submitDebounce(() => submitConfirmDialog(postDataCB))}
          disabled={_.isEqual(data, backupData)}
        >
          Submit
        </PrimaryButton>
      </SwButtonSection>
    </>
  );
};

export default ADSendToPacs;
